import React, { ComponentProps, createRef, MouseEvent } from 'react'
import classNames from 'classnames'
import LoadingPlaceholder from '../LoadingPlaceholder'
import { Type, Variant } from './types'
import {
  IconWrapper,
  ButtonWrapper,
  getButton,
  BaseButton,
  LoadingWrapper,
} from './Button.styles'

export interface BaseButtonProps {
  /** Set the loading state */
  loading?: boolean
  /** For styling purposes via `styled-components` */
  className?: string
  /** Set the button style. By default `tinted` you can also use the `untinted` versions */
  variant?: Variant
  /** To use an icon `along` the button. For using just the icon use it as `children` */
  icon?: AnyComponent
  /** Set the wrapper for the button */
  contentWrapper?: AnyComponent
  /** The children node */
  children?: React.ReactChild
  /** Distinguish between regular `button`, `submit` a form or `reset` */
  type?: Type
  /** Set disabled state */
  disabled?: boolean
  /** onClick handler function */
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void
  /** This will apply a fontSizes.small and max-height: 40px */
  small?: boolean
  /** This will apply a fontSizes.smaller and max-height: 32px */
  smaller?: boolean
  /** This will apply a fontSizes.tiny */
  tiny?: boolean
  /** This will fill the 100% of the container width */
  fill?: boolean
}

export type ButtonProps = BaseButtonProps & ComponentProps<typeof BaseButton>

const isComponentWithRef = (child: any) =>
  child &&
  React.isValidElement<React.ComponentPropsWithRef<AnyComponent>>(child)

/**
 * Button style is determined by `variant` prop, there are 7 possible values:
 *
 * 1. primary
 * 2. secondary
 * 3. tertiary
 * 4. primary-untinted
 * 5. secondary-untinted
 * 6. negative
 * 7. negative-secondary
 *
 * The tinted versions are the default ones, because we will use them quite more often than the untinted ones.
 *
 * When dealing with Icons, you can use the `icon` prop if you will use the icon along with a text. For using just a button, please check [these][button with icon] recipes
 */
const Button = ({
  loading,
  children,
  className,
  variant = 'secondary',
  icon: Icon,
  contentWrapper: ContentWrapper = ButtonWrapper,
  type = 'button',
  disabled,
  ...props
}: ButtonProps) => {
  const childRef = createRef<HTMLElement>()

  const StyledButton = getButton(variant)
  /**
   * Renders the child component while keeping a reference to it. This way we
   * can proxy all click events sent to the button to the child.
   * @param child Child element we are about to insert
   */
  const renderChildren = (child: any) => {
    if (isComponentWithRef(child)) {
      return React.cloneElement(child, { ref: childRef })
    }

    return child
  }

  const onButtonClick = () => {
    // Send click event to the child component
    if (typeof childRef.current?.click === 'function') {
      childRef.current?.click()
    }
  }

  const primary =
    variant === 'primary' ||
    variant === 'primary-untinted' ||
    variant === 'negative'

  return (
    <StyledButton
      className={classNames('Button', className)}
      disabled={loading || disabled}
      loading={loading}
      variant={variant}
      type={type}
      onClick={onButtonClick}
      /**
       * Hack to force refresh styles in Safari
       * after transitioning from disabled to another style
       */
      key={
        (window.safari && disabled && (`${Math.random()}` as React.Key)) ||
        undefined
      }
      {...props}>
      {Icon && (
        <IconWrapper>
          <Icon />
        </IconWrapper>
      )}

      <ContentWrapper>
        {React.Children.map(children, renderChildren)}
      </ContentWrapper>

      {loading && (
        <LoadingWrapper data-testid="button-spinner">
          <LoadingPlaceholder primary={primary} size="24px" />
        </LoadingWrapper>
      )}
    </StyledButton>
  )
}

export default Button
